"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Plus, Building, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useSuppliers } from "@/hooks/useSuppliers";
import { supabase } from "@/integrations/supabase/client";

// Schema simplificado para cadastro rápido
const quickAddSupplierSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  documentNumber: z.string().optional(),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phone: z.string().optional(),
});

type QuickAddSupplierSchema = z.infer<typeof quickAddSupplierSchema>;

interface QuickAddSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSupplierAdded: (supplierId: string) => void;
}

export function QuickAddSupplierModal({ isOpen, onClose, onSupplierAdded }: QuickAddSupplierModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const { createSupplier } = useSuppliers();

  const form = useForm<QuickAddSupplierSchema>({
    resolver: zodResolver(quickAddSupplierSchema),
    defaultValues: {
      name: "",
      documentNumber: "",
      email: "",
      phone: "",
    },
  });

  const handleSubmit = async (data: QuickAddSupplierSchema) => {
    setIsSaving(true);

    try {
      // Obter o usuário atual
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast.error("Usuário não autenticado");
        return;
      }

      // Criar fornecedor com dados simplificados
      const supplierData = {
        name: data.name,
        documentType: "cnpj" as const,
        documentNumber: data.documentNumber?.trim() || `EMPTY-PLACEHOLDER-${Date.now()}`,
        email: data.email || "",
        phone: data.phone || "",
        addressStreet: "",
        addressNumber: "",
        addressComplement: "",
        addressNeighborhood: "",
        addressCity: "",
        addressState: "",
        addressZipCode: "",
        bankName: "",
        bankAgency: "",
        bankAccount: "",
        paymentTerms: "",
        notes: "",
        isActive: true,
      };

      const newSupplier = await createSupplier({ formData: supplierData, userId: userData.user.id });

      // Passar o ID do fornecedor criado para o formulário principal
      onSupplierAdded(newSupplier.id);

      // Resetar formulário e fechar modal
      form.reset();
      onClose();

      toast.success("Fornecedor adicionado com sucesso!");
    } catch (error: any) {
      console.error("Erro ao criar fornecedor:", error);
      toast.error(error.message || "Erro ao criar fornecedor");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Adicionar Novo Fornecedor
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Fornecedor *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Digite o nome do fornecedor"
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="documentNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNPJ/CPF</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="00.000.000/0000-00"
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="fornecedor@exemplo.com"
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="(00) 00000-0000"
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Fornecedor
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
